'use client'

import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { type ReactNode, useMemo, useState } from 'react'

import { Checkbox } from '@/components/ui/checkbox'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ExchangeIcon } from '@/components/widget/exchange-icon'
import { useAccountLookupMap } from '@/features/dashboard/hooks/use-account-exchange-map'
import {
  useLinkingGroupActions,
  useLinkingGroupPositionFilter,
} from '@/hooks/use-linking-group-state'
import { getExchangeIconKey, getExchangeLabel } from '@/lib/configs/exchange-registry'
import { cn } from '@/lib/utils'
import { useActiveAccountIds } from '@/service/account.hook'
import { Exchange } from '@/types/generated/common/types'

interface AccountFilterDropdownProps {
  cardId: string
  accountIds?: string[]
  selectionMode?: 'single' | 'multiple'
  selection?: {
    /** `null` means every active account, while `[]` is an explicit empty selection. */
    selectedIds: string[] | null
    onChange: (selectedIds: string[] | null) => void
  }
}

interface AccountOption {
  id: string
  name: string
  exchange: Exchange | undefined
  iconKey: ReturnType<typeof getExchangeIconKey>
}

/**
 * `matchesAccountFilter` (and position-table.tsx/history-table.tsx's own copies of the
 * same check) treat an empty `value` array as "no filter — show every account", the same
 * as `disabled: true`. That's the right default, but it means a real "deselect everything"
 * can't be written as `[]` — it would just be read back as "select all" again, so the
 * Select All checkbox could never be unchecked. Storing this sentinel as the sole value
 * instead means "empty" is written as one value that matches no real account id, so
 * every consumer's own `includes(accountId)` check already excludes everything correctly
 * with no changes needed on the read side.
 */
const NONE_SELECTED_SENTINEL = '__none__'

// Same selected look as the watchlist wizard's instrument rows: tinted row + emphasis edge,
// with the check shown only on selected rows.
function SelectableRow({
  selected,
  onToggle,
  className,
  children,
}: {
  selected: boolean
  onToggle: (selected: boolean) => void
  className?: string
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={() => onToggle(!selected)}
      className={cn(
        'relative flex h-8 w-full cursor-pointer items-center gap-1 px-3 text-left',
        selected ? 'bg-foreground/5' : 'hover:bg-foreground/4',
        className
      )}
    >
      {selected && (
        <span
          aria-hidden="true"
          className="bg-emphasis pointer-events-none absolute inset-y-0 left-0 w-0.5"
        />
      )}
      {children}
      {selected && <CheckIcon className="text-foreground size-4 shrink-0" />}
    </button>
  )
}

function AccountRow({
  option,
  checked,
  onToggle,
}: {
  option: AccountOption
  checked: boolean
  onToggle: (checked: boolean) => void
}) {
  return (
    <SelectableRow selected={checked} onToggle={onToggle} className="pl-5">
      {option.iconKey && <ExchangeIcon exchange={option.iconKey} className="size-3.5 shrink-0" />}
      <span
        className={cn(
          'flex-1 truncate px-1 text-sm tracking-tight',
          checked ? 'text-foreground' : 'text-foreground/60'
        )}
      >
        {option.name}
      </span>
    </SelectableRow>
  )
}

/**
 * Top-level account filter for the Positions widget — reads and writes the same
 * `positionFilter.accounts` slot position-table.tsx's own (fuller instrument/expiry/
 * asset) filter already uses, via the shared linking-group state. Applies to Positions,
 * Active, and History alike (see account-filter.ts's matchesAccountFilter, used by
 * use-active-rows.ts/use-unified-history-rows.ts). Multi-select, grouped by exchange —
 * every group is expanded by default; a `collapsedExchanges` set (rather than the
 * inverse) means a newly-appeared exchange group defaults to expanded too.
 */
export default function AccountFilterDropdown({
  cardId,
  accountIds,
  selectionMode = 'multiple',
  selection,
}: AccountFilterDropdownProps) {
  const t = useTranslations('trade.positionFilter')
  const positionFilter = useLinkingGroupPositionFilter(cardId)
  const { setPositionFilter } = useLinkingGroupActions(cardId)
  const accounts = positionFilter.accounts
  const activeAccountIds = useActiveAccountIds()
  const accountLookup = useAccountLookupMap()
  const [isOpen, setIsOpen] = useState(false)
  const [collapsedExchanges, setCollapsedExchanges] = useState<Set<Exchange>>(new Set())

  const options = useMemo<AccountOption[]>(() => {
    const allowedIds = accountIds ? new Set(accountIds) : null
    return activeAccountIds
      .filter((id) => allowedIds === null || allowedIds.has(id))
      .map((id) => {
        const entry = accountLookup.get(id)
        return {
          id,
          name: entry?.name ?? id,
          exchange: entry?.exchange,
          iconKey: entry ? getExchangeIconKey(entry.exchange) : undefined,
        }
      })
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [accountIds, activeAccountIds, accountLookup])

  const groups = useMemo(() => {
    const byExchange = new Map<Exchange, AccountOption[]>()
    for (const option of options) {
      if (option.exchange === undefined) continue
      const list = byExchange.get(option.exchange) ?? []
      list.push(option)
      byExchange.set(option.exchange, list)
    }
    return Array.from(byExchange.entries()).map(([exchange, groupOptions]) => ({
      exchange,
      label: getExchangeLabel(exchange),
      options: groupOptions,
    }))
  }, [options])

  const rawValues = selection ? (selection.selectedIds ?? []) : accounts.value || []
  const isExplicitlyNone = selection
    ? selection.selectedIds !== null && selection.selectedIds.length === 0
    : !accounts.disabled && rawValues.length === 1 && rawValues[0] === NONE_SELECTED_SENTINEL
  const optionIdSet = new Set(options.map((option) => option.id))
  const selectedValues = isExplicitlyNone ? [] : rawValues.filter((id) => optionIdSet.has(id))
  const isAll = selection
    ? selection.selectedIds === null
    : !isExplicitlyNone &&
      (accounts.disabled || selectedValues.length === 0 || selectedValues.includes('ALL'))
  const selectedSet = new Set(isAll ? options.map((o) => o.id) : selectedValues)
  const selectedCount = selectedSet.size
  const isEffectivelyAll =
    selectionMode === 'multiple' &&
    (isAll || (options.length > 0 && selectedCount === options.length))
  const singleSelected =
    selectedCount === 1 ? options.find((o) => selectedSet.has(o.id)) : undefined
  const selectedGroup =
    selectedCount > 1
      ? groups.find(
          (group) =>
            group.options.filter((option) => selectedSet.has(option.id)).length === selectedCount
        )
      : undefined
  const isEntireSelectedGroup = selectedGroup?.options.length === selectedCount
  const selectedExchangeIconKey =
    singleSelected?.iconKey ??
    (selectedGroup ? getExchangeIconKey(selectedGroup.exchange) : undefined)

  const setSelection = (ids: string[]) => {
    if (selection) {
      selection.onChange(selectionMode === 'multiple' && ids.length === options.length ? null : ids)
      return
    }
    setPositionFilter({
      ...positionFilter,
      accounts: { disabled: false, value: ids.length > 0 ? ids : [NONE_SELECTED_SENTINEL] },
    })
  }

  const toggleAccount = (id: string, checked: boolean) => {
    if (selectionMode === 'single') {
      if (!selectedSet.has(id)) setSelection([id])
      setIsOpen(false)
      return
    }
    const next = new Set(selectedSet)
    if (checked) next.add(id)
    else next.delete(id)
    setSelection(Array.from(next))
  }

  const toggleSelectAll = (checked: boolean) => {
    setSelection(checked ? options.map((o) => o.id) : [])
  }

  const toggleGroup = (exchange: Exchange) => {
    setCollapsedExchanges((prev) => {
      const next = new Set(prev)
      if (next.has(exchange)) next.delete(exchange)
      else next.add(exchange)
      return next
    })
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t('accountsTitle')}
          disabled={options.length === 0}
          className="hover:bg-foreground/10 flex h-7 w-auto max-w-32 cursor-pointer items-center gap-1 rounded-sm border-none bg-transparent px-2 text-xs transition-colors"
          onMouseDown={(e) => e.stopPropagation()}
          onDoubleClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
        >
          <div className="flex min-w-0 items-center gap-1.5">
            {selectedExchangeIconKey && (
              <ExchangeIcon exchange={selectedExchangeIconKey} className="size-3.5 shrink-0" />
            )}
            <span className="text-foreground truncate">
              {singleSelected
                ? singleSelected.name
                : selectedGroup && isEntireSelectedGroup
                  ? t('allVenueAccounts', { venue: selectedGroup.label })
                  : selectedGroup
                    ? t('venueAccountsSelectedCount', {
                        venue: selectedGroup.label,
                        count: selectedCount,
                      })
                    : isEffectivelyAll
                      ? t('allAccounts')
                      : t('accountsSelectedCount', { count: selectedCount })}
            </span>
            {isOpen ? (
              <ChevronUpIcon className="text-foreground/50 size-3.5 shrink-0" />
            ) : (
              <ChevronDownIcon className="text-foreground/50 size-3.5 shrink-0" />
            )}
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="no-drag max-h-100 w-52 overflow-y-auto p-0"
        onDoubleClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
      >
        <div className="border-foreground/10 flex flex-col border-b py-1">
          <span className="text-foreground/60 flex h-7 items-center px-3 text-xs font-medium tracking-tight">
            {t('accountsTitle')}
          </span>
          {selectionMode === 'multiple' && (
            <div
              className="focus-row flex h-8 cursor-pointer items-center gap-1 px-3"
              onClick={() => toggleSelectAll(!isEffectivelyAll)}
            >
              <span className="flex-1 truncate px-1 text-sm font-medium tracking-tight">
                {t('selectAll')}
              </span>
              <Checkbox
                className="focus-row-target size-5"
                checked={isEffectivelyAll}
                onCheckedChange={(checked) => {
                  if (typeof checked === 'boolean') toggleSelectAll(checked)
                }}
                onClick={(event) => event.stopPropagation()}
              />
            </div>
          )}
        </div>

        {groups.map((group) => {
          const expanded = !collapsedExchanges.has(group.exchange)
          return (
            <div
              className="border-foreground/5 flex flex-col items-start border-b py-1 last:border-b-0"
              key={group.exchange}
            >
              <button
                className="flex h-7 w-full cursor-pointer items-center justify-between px-3"
                onClick={() => toggleGroup(group.exchange)}
              >
                <div className="flex items-center gap-1.5">
                  <span className="text-foreground/50 text-[11px] font-semibold tracking-wide uppercase">
                    {group.label}
                  </span>
                  <span className="bg-foreground/10 text-foreground flex h-4 min-w-4 items-center justify-center px-1 text-[11px] font-semibold tracking-tight">
                    {group.options.length}
                  </span>
                </div>
                {expanded ? (
                  <ChevronUpIcon className="text-foreground/40 size-3.5 shrink-0" />
                ) : (
                  <ChevronDownIcon className="text-foreground/40 size-3.5 shrink-0" />
                )}
              </button>
              {expanded && (
                <ul className="flex w-full list-none flex-col items-stretch">
                  {group.options.map((option) => (
                    <li key={option.id}>
                      <AccountRow
                        option={option}
                        checked={selectedSet.has(option.id)}
                        onToggle={(next) => toggleAccount(option.id, next)}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )
        })}
      </PopoverContent>
    </Popover>
  )
}
